import type { IService, IServicePriceSettings, IServicePriceSettingsParam } from '@types'
import { ServiceFormats, ServicePriceSettingsActions, ServiceTypes } from '@types'
import { hmToTMinutes } from '../minutes'

export function preparePriceSettings(settings: IServicePriceSettings[] = []) {
  return (
    settings?.map(s => ({
      ...s,
      dateStart: s.dateStart && new Date(s.dateStart),
      dateEnd: s.dateEnd && new Date(s.dateEnd),
    })) || []
  )
}

export function clearDate(date: Date | string) {
  const _date = new Date(date)
  return new Date(_date.getFullYear(), _date.getMonth(), _date.getDate())
}

export function filterActualPriceSettings(settings: IServicePriceSettings[] = []) {
  const today = clearDate(new Date())
  return settings
    .map(s => ({ ...s, parameters: s.parameters.filter(p => !p.isDisabled) }))
    .filter(s => s.parameters.length)
    .filter(
      s => s.action === ServicePriceSettingsActions.Const || !s.dateEnd || +today <= +clearDate(new Date(s.dateEnd)),
    )
  // TODO if (today - dateEnd < 7 days) check on weekdays
}

function getMinRentPrice(p: IServicePriceSettingsParam) {
  const durationMinutes = hmToTMinutes(p.durationMinutes, p.durationHour)
  const timeDivider = Math.min(durationMinutes, 60) / 60
  return +p.amount * timeDivider
}

export function getGetPriceFunction(type: ServiceTypes): (p: IServicePriceSettingsParam) => number {
  switch (type) {
    case ServiceTypes.Rent:
      return getMinRentPrice
    default:
      return (p: IServicePriceSettingsParam) => +p.amount
  }
}

export function filterPriceSettingsByTarget(settings: IServicePriceSettings[] = [], targetId?: number | string) {
  return targetId !== undefined ? settings.filter(s => String(s.target?.id) === String(targetId)) : settings
}

export function getBookableTargetsByService(service: IService) {
  if (service.format === ServiceFormats.without) {
    return service.targets || []
  }

  const settings = filterActualPriceSettings(preparePriceSettings(service.priceSettings))
  return service.targets?.filter(t => filterPriceSettingsByTarget(settings, t.id).length) || []
}

export function getMinPriceByPriceSettings(
  settings: IServicePriceSettings[] = [],
  type: ServiceTypes,
  targetId?: number | string,
) {
  const getPrice = getGetPriceFunction(type)
  const filteredSettings = filterPriceSettingsByTarget(settings, targetId)
  const minPrice = filteredSettings.reduce(
    (r, s) =>
      Math.min(
        r,
        s.parameters.reduce((res, p) => Math.min(res, getPrice(p)), Number.MAX_SAFE_INTEGER),
      ),
    Number.MAX_SAFE_INTEGER,
  )
  return minPrice === Number.MAX_SAFE_INTEGER ? 0 : minPrice
}

export function getMinPriceByService(service: IService, targetId?: number | string) {
  return getMinPriceByPriceSettings(
    filterActualPriceSettings(preparePriceSettings(service.priceSettings)),
    service.type,
    targetId,
  )
}
